/**
 * Отбор по диапазону дат ОДНОЙ КНОПКОЙ: готовые окна и календарь месяца.
 *
 * Копия `DateRangePicker` из supply (`/claude/claude/supply/web`) — замечание
 * разметкой 26.09.26 на `/calls?tab=list`: «не два поля, а нормальный
 * daypicker, как в supply: вчера, 7 дней, год и период». Две `input type="date"`
 * рисуют СИСТЕМНЫЙ попап браузера и занимают тулбар вдвое.
 *
 * Правка применяется ВТОРЫМ КЛИКОМ календаря, без кнопки «применить». Даты —
 * строками `YYYY-MM-DD` через `lib/dates.ts`. Окна по умолчанию смотрят НАЗАД:
 * звонки, лиды и сделки — вопрос про прошедшие дни.
 */

import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RangeCalendar } from "@/components/RangeCalendar";
import { isValidDate, iso, plusDays } from "@/lib/dates";

export type Окно = { label: string; hint: string; from: string; to: string };

/** Ширина попапа, px: по ней решаем, влезет ли он вправо от кнопки. */
const ШИРИНА = 264;

/** Окна «назад от сегодня». Считаются на каждый рендер: вкладку держат днями. */
export function прошлыеОкна(): Окно[] {
  const today = new Date();
  const t = iso(today);
  const y = iso(plusDays(today, -1));
  return [
    { label: "сегодня", hint: "только сегодня", from: t, to: t },
    { label: "вчера", hint: "только вчера", from: y, to: y },
    { label: "7 дней", hint: "6 дней назад и сегодня", from: iso(plusDays(today, -6)), to: t },
    { label: "30 дней", hint: "29 дней назад и сегодня", from: iso(plusDays(today, -29)), to: t },
    {
      label: "этот месяц",
      hint: "с первого числа по сегодня",
      from: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: t,
    },
    { label: "год", hint: "364 дня назад и сегодня", from: iso(plusDays(today, -364)), to: t },
    { label: "2 года", hint: "729 дней назад и сегодня", from: iso(plusDays(today, -729)), to: t },
  ];
}

/** «24.09», а у чужого года «24.09.25». */
function краткая(value: string): string {
  const d = value.slice(8, 10);
  const m = value.slice(5, 7);
  const y = value.slice(0, 4);
  return y === String(new Date().getFullYear()) ? `${d}.${m}` : `${d}.${m}.${y.slice(2)}`;
}

/** Подпись кнопки: узнанное окно — своим именем, один день — датой, иначе «с — по». */
function подпись(from: string, to: string, список: Окно[]): string {
  if (!from && !to) return "все";
  const узнано = список.find((o) => o.from === from && o.to === to);
  if (узнано) return узнано.label;
  if (from === to) return краткая(from);
  if (!to) return `с ${краткая(from)}`;
  if (!from) return `по ${краткая(to)}`;
  return `${краткая(from)} — ${краткая(to)}`;
}

/** Строка списка окон; место под галочку держится, чтобы строки не разъезжались. */
function Строка({ label, title, on, onClick }: { label: string; title: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={
        "flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-bg-hover " +
        (on ? "text-accent-text" : "text-fg")
      }
    >
      <span className="truncate">{label}</span>
      {on ? <Check size={12} className="ml-auto flex-none" /> : null}
    </button>
  );
}

export function DateRangePicker({
  label,
  from: fromRaw,
  to: toRaw,
  onChange,
  windows,
  allowAll = false,
  single = false,
}: {
  label: string;
  /** Начало включительно, `YYYY-MM-DD`. */
  from: string;
  /** Конец включительно. */
  to: string;
  onChange: (from: string, to: string) => void;
  windows?: Окно[];
  /** Строка «все» — снять отбор. Звонкам без дат нельзя: это вся база. */
  allowAll?: boolean;
  /** Отбор ОДНОГО дня (рейтинг «Кто не взял» — за день): окна «сегодня»/«вчера», клик по дню
   *  календаря сразу применяет его обеими границами. */
  single?: boolean;
}) {
  // Даты приходят из адреса, а адрес правят руками: некалендарная граница — незаданная.
  const from = isValidDate(fromRaw) ? fromRaw : "";
  const to = isValidDate(toRaw) ? toRaw : "";

  const [open, setOpen] = useState(false);
  // Кнопка у правого края страницы — попап открывается влево от неё: иначе календарь уходит за край
  // экрана и конец недели не выбрать («AI УЦ» #60, «Активность → Автоакцепт»).
  const [кПравому, setКПравому] = useState(false);
  // Невысокий экран: попап не длиннее места под кнопкой, остальное — прокруткой внутри него.
  const [высота, setВысота] = useState<number | undefined>(undefined);
  const boxRef = useRef<HTMLDivElement>(null);
  const список = windows ?? (single ? прошлыеОкна().slice(0, 2) : прошлыеОкна());

  // Клик мимо и Escape закрывают. `mousedown`: щелчок по соседнему отбору должен и закрыть, и сработать.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const выбрать = (nextFrom: string, nextTo: string) => {
    onChange(nextFrom, nextTo);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      <button
        type="button"
        onClick={() => {
          const r = boxRef.current?.getBoundingClientRect();
          setКПравому(!!r && r.left + ШИРИНА + 8 > document.documentElement.clientWidth);
          setВысота(r ? Math.max(320, window.innerHeight - r.bottom - 12) : undefined);
          setOpen((было) => !было);
        }}
        title={from || to ? `${from || "…"} — ${to || "…"} включительно` : "без отбора по дате"}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
          {label}
        </span>
        <span className="truncate px-2 tabular-nums text-fg">{подпись(from, to, список)}</span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        <div
          style={{ width: ШИРИНА, maxHeight: высота }}
          className={`absolute top-[34px] ${кПравому ? "right-0" : "left-0"} z-30 overflow-x-hidden overflow-y-auto rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg`}
        >
          <div className="py-1">
            {список.map((окно) => (
              <Строка
                key={окно.label}
                label={окно.label}
                title={окно.hint}
                on={окно.from === from && окно.to === to}
                onClick={() => выбрать(окно.from, окно.to)}
              />
            ))}
            {allowAll && <Строка label="все" title="снять отбор по дате" on={!from && !to} onClick={() => выбрать("", "")} />}
          </div>
          <div className="border-t border-border">
            <div className="px-2.5 pt-2 text-[11.5px] text-fg-faint">{single ? "другой день" : "свой период"}</div>
            <RangeCalendar from={from} to={to} onPick={выбрать} single={single} {...(single && { max: iso(new Date()) })} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
